import { ConversationPlatform } from "@redotech/redo-model/conversation";
import { Condition } from "@redotech/redo-model/return-flow/condition";
import { ConditionState } from "@redotech/redo-return-ui/flows/return-flow/condition";
import {
  DetailsComponentsForRuleConditions,
  MultipleConditionsState,
} from "@redotech/redo-return-ui/flows/rules/multiple-conditions";
import { TICKET_CHANNEL } from "@redotech/redo-return-ui/flows/rules/ticket-channel";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { Button, ButtonSize, ButtonTheme } from "@redotech/redo-web/button";
import { Divider } from "@redotech/redo-web/divider";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { SetStateAction, memo } from "react";
import { ConditionDetails } from "../return-flow/condition";
import { AfterHoursDetails } from "../return-flow/condition-types/after-hours";
import { AssignmentTypeDetails } from "./conditions/assignment-type";
import { MessageBodyMatchesDetails } from "./conditions/message-body-matches";
import { SentFromEmailDetails } from "./conditions/sent-from-email";
import { SubjectLineDetails } from "./conditions/subject-line";
import { ReceivedAtEmailDetails } from "./received-at-email";
import { TicketChannelDetails } from "./ticket-channel";
import { TopicDetails } from "./topic";

export const MultipleConditionsDetails = memo(
  function MultipleConditionsDetails({
    stepSelect,
    state,
    setState,
    flowType,
    step,
    allSteps,
    setStateWithId,
  }: StepTypeDetailsProps<MultipleConditionsState>) {
    const handleAddCondition = () => {
      setState((oldState) => {
        return {
          ...oldState,
          conditions: [
            ...oldState.conditions,
            {
              conditionType: TICKET_CHANNEL(TicketChannelDetails),
              conditionState: TICKET_CHANNEL(TicketChannelDetails).fromModel({
                type: Condition.TICKET_CHANNEL,
                channels: [ConversationPlatform.INSTAGRAM],
              }),
            },
          ],
        };
      });
    };
    const handleDeleteCondition = (index: number) => {
      setState((oldState) => {
        return {
          ...oldState,
          conditions: oldState.conditions.filter((_, ind) => ind !== index),
        };
      });
    };
    return (
      <>
        {state.conditions.map((condition, index) => (
          <>
            <Divider />
            <ConditionDetails
              allSteps={allSteps}
              flowType={flowType}
              onDeleteChild={() => handleDeleteCondition(index)}
              setState={(
                updateSingleCondition: SetStateAction<ConditionState>,
              ) => {
                let updatedCondition: ConditionState;
                if (typeof updateSingleCondition === "function") {
                  const updateConditionState = updateSingleCondition(condition);
                  if (
                    typeof updateConditionState.conditionState === "function"
                  ) {
                    const updatedConditionState =
                      updateConditionState.conditionState(
                        condition.conditionState,
                      );
                    updatedCondition = {
                      ...updatedConditionState,
                      conditionState: updatedConditionState,
                    };
                  } else {
                    updatedCondition = updateConditionState;
                  }
                } else {
                  updatedCondition = updateSingleCondition;
                }
                setState((oldState) => {
                  return {
                    ...oldState,
                    conditions: oldState.conditions.map((cond, ind) => {
                      if (index === ind) {
                        return updatedCondition;
                      } else {
                        return cond;
                      }
                    }),
                  };
                });
              }}
              setStateWithId={setStateWithId}
              state={condition}
              step={step}
              stepSelect={stepSelect}
            />
          </>
        ))}
        <LabeledInput
          errors={
            state.conditions.length > 0
              ? []
              : ["At least one condition is required"]
          }
          label=""
        >
          <Button
            onClick={handleAddCondition}
            size={ButtonSize.MEDIUM}
            theme={ButtonTheme.PRIMARY}
          >
            + Add condition
          </Button>
        </LabeledInput>
        <LabeledInput
          errors={
            state.nextTrue || state.nextFalse
              ? []
              : ["At least one of these is required"]
          }
          label="If matches, then"
        >
          {stepSelect({
            value: state.nextTrue,
            valueChange: (nextTrue) =>
              setState((state) => ({ ...state, nextTrue })),
          })}
        </LabeledInput>
        <LabeledInput
          errors={
            state.nextTrue || state.nextFalse
              ? []
              : ["At least one of these is required"]
          }
          label="If does not match, then"
        >
          {stepSelect({
            value: state.nextFalse,
            valueChange: (nextFalse) =>
              setState((state) => ({ ...state, nextFalse })),
          })}
        </LabeledInput>
      </>
    );
  },
);

export const DetailsForRuleConditions: DetailsComponentsForRuleConditions = {
  TicketChannelDetails,
  AfterHoursDetails,
  SubjectLineDetails,
  MessageBodyMatchesDetails,
  TopicDetails,
  ReceivedAtEmailDetails,
  SentFromEmailDetails,
  AssignmentTypeDetails,
};
